import { SLUG_PATTERN } from "@agentcall/core/type/admin_organization_type"
import { zodResolver } from "@hookform/resolvers/zod"
import { useEffect } from "react"
import { useForm } from "react-hook-form"
import * as z from "zod"
import { organizationApi } from "@/api/client/organization"
import type { OrganizationList200OrganizationsItem } from "@/api/generated/models"
import { getErrorMessage } from "@/api/instance"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { toast } from "@/hooks/use-toast"

const organizationFormSchema = z.object({
  name: z
    .string()
    .min(1, "組織名を入力してください")
    .max(255, "組織名は255文字以内で入力してください"),
  slug: z
    .string()
    .min(1, "スラッグを入力してください")
    .max(255, "スラッグは255文字以内で入力してください")
    .regex(SLUG_PATTERN, "英小文字または数字で始まり、英小文字・数字・ハイフンのみ使用できます"),
})

type OrganizationFormValues = z.infer<typeof organizationFormSchema>

interface OrganizationFormModalProps {
  isOpen: boolean
  onClose: () => void
  organization: OrganizationList200OrganizationsItem | null
  onRefetch: () => void
}

export function OrganizationFormModal({
  isOpen,
  onClose,
  organization,
  onRefetch,
}: OrganizationFormModalProps) {
  const isEdit = organization !== null

  const form = useForm<OrganizationFormValues>({
    resolver: zodResolver(organizationFormSchema),
    defaultValues: {
      name: "",
      slug: "",
    },
  })

  useEffect(() => {
    if (!isOpen) return
    form.reset({
      name: organization?.name ?? "",
      slug: organization?.slug ?? "",
    })
  }, [isOpen, organization, form])

  const createOrganizationMutation = organizationApi.useOrganizationCreate({
    mutation: {
      onError: (error) =>
        toast({
          title: "組織の作成に失敗しました",
          description: getErrorMessage(error),
          variant: "destructive",
        }),
    },
  })
  const updateOrganizationMutation = organizationApi.useOrganizationUpdate({
    mutation: {
      onError: (error) =>
        toast({
          title: "組織の更新に失敗しました",
          description: getErrorMessage(error),
          variant: "destructive",
        }),
    },
  })
  const isSubmitting = createOrganizationMutation.isPending || updateOrganizationMutation.isPending

  const onSubmit = (data: OrganizationFormValues) => {
    const mutationCallbacks = {
      onSuccess: () => {
        toast({
          title: isEdit ? "組織を更新しました" : "組織を作成しました",
        })
        onRefetch()
        onClose()
      },
    }

    if (isEdit) {
      updateOrganizationMutation.mutate(
        {
          id: organization.id,
          data: {
            name: data.name,
            slug: data.slug,
          },
        },
        mutationCallbacks
      )
      return
    }

    createOrganizationMutation.mutate(
      {
        data: {
          name: data.name,
          slug: data.slug,
        },
      },
      mutationCallbacks
    )
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[500px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "組織の編集" : "組織の追加"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "組織名とスラッグを変更します。所属する管理者とロールはそのまま引き継がれます。"
              : "管理者とロールをまとめる単位です。ロールは空の状態で作成されるため、「ロール管理」から追加してください。"}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>組織名</FormLabel>
                  <FormControl>
                    <Input type="text" placeholder="関西支社" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="slug"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>スラッグ</FormLabel>
                  <FormControl>
                    <Input type="text" placeholder="kansai" {...field} />
                  </FormControl>
                  <FormDescription>
                    英小文字または数字で始まり、英小文字・数字・ハイフンのみ使用できます。組織を識別する値のため重複できません。
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="submit" variant="default" disabled={isSubmitting}>
                {isEdit ? "更新する" : "作成する"}
              </Button>
              <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
                キャンセル
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
